/* eslint-disable no-param-reassign,consistent-return,no-shadow */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { message, Modal } from "antd";
import {
  propertyTypesRoute,
  propertyTypesPageSearchRoute,
  propertyTypeByObjectTypeRoute,
  propertyTypeByTypeIdRoute,
} from "../../lib/routes/property-type-admin";
import { PageableSearchResult, PropertyTypeSearchParams, PropertyType } from "../../lib/interfaces";
import { LoadingStatusCode, ObjectTypeSysname } from "../../lib/types";
import { catchApiError } from "../../lib/utils/catchApiError";

interface InitialState {
  propertyTypes: PropertyType[];
  propertyTypesLoading: LoadingStatusCode;
  propertyPageSearchTypes?: PageableSearchResult<PropertyType[]>;
  propertyPageSearchTypesLoading: LoadingStatusCode;
  propertyTypesByObjectType: PropertyType[];
  propertyTypesByObjectTypeLoading: LoadingStatusCode;
  organizationPropertyTypes: PropertyType[];
  organizationPropertyTypesLoading: LoadingStatusCode;
}

const initialState: InitialState = {
  propertyTypes: [],
  propertyTypesLoading: "idle",
  propertyPageSearchTypesLoading: "idle",
  propertyTypesByObjectType: [],
  propertyTypesByObjectTypeLoading: "idle",
  organizationPropertyTypes: [],
  organizationPropertyTypesLoading: "idle",
};

export const fetchPropertyTypes = createAsyncThunk<PropertyType[]>(
  "property-types/fetchPropertyTypes",
  async () => {
    try {
      const response = axios.get<PropertyType[]>(propertyTypesRoute());
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки свойств. Попробуйте позже",
      });
    }
  }
);

export const fetchPropertyTypesByObjectType = createAsyncThunk<
  PropertyType[],
  { objectTypeSysname: ObjectTypeSysname }
>("property-types/fetchPropertyTypesByObjectType", async ({ objectTypeSysname }) => {
  try {
    const url = propertyTypeByObjectTypeRoute(objectTypeSysname);
    const { data } = await axios.get<PropertyType[]>(url);
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки свойств. Попробуйте позже",
    });
  }
});

export const fetchPropertyPageSearchTypes = createAsyncThunk<
  PageableSearchResult<PropertyType[]>,
  PropertyTypeSearchParams
>("property-types/fetchPropertyPageSearchTypes", async (params) => {
  try {
    const url = propertyTypesPageSearchRoute(params);
    const { data } = await axios.get<PageableSearchResult<PropertyType[]>>(url);
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки свойств. Попробуйте позже",
    });
  }
});

export const createPropertyTypes = createAsyncThunk<
  PropertyType,
  { objectTypeSysname: ObjectTypeSysname; dataPost: Partial<PropertyType> }
>("property-types/createPropertyTypes", async (data) => {
  const { objectTypeSysname, dataPost } = data;
  try {
    message.loading("Сохранение", 0);
    const response = axios.post<PropertyType>(
      propertyTypeByObjectTypeRoute(objectTypeSysname),
      dataPost
    );
    const { data } = await response;
    message.destroy();
    message.success("Свойство успешно добавлено");
    return data;
  } catch (e) {
    message.destroy();
    return catchApiError({
      e,
      title: "Ошибка создания",
    });
  }
});

export const deletePropertyType = createAsyncThunk<
  Promise<any>,
  { objectTypeSysname: ObjectTypeSysname; propertyTypeAlias: string }
>("property-types/deletePropertyType", async (props) => {
  try {
    message.loading("Удаление", 0);
    const response = axios.delete(propertyTypeByTypeIdRoute(props));
    const { data } = await response;
    message.destroy();
    message.success("Свойство успешно удалено");
    return data;
  } catch (e) {
    message.destroy();
    return catchApiError({
      e,
      title: "Ошибка удаления свойства",
    });
  }
});

export const updatePropertyType = createAsyncThunk<
  PropertyType,
  { objectTypeSysname: ObjectTypeSysname; propertyTypeAlias: string; dataPost: PropertyType }
>("property-types/updatePropertyType", async (data) => {
  const { dataPost, ...props } = data;
  try {
    message.loading("Сохранение", 0);
    const response = axios.patch(propertyTypeByTypeIdRoute(props), dataPost);
    const { data } = await response;
    message.destroy();
    message.success("Свойство успешно отредактировано");
    return data;
  } catch (e) {
    message.destroy();
    return catchApiError({
      e,
      title: "Ошибка изменения свойства",
    });
  }
});

export const fetchOrganizationPropertyTypes = createAsyncThunk<PropertyType[]>(
  "property-types/fetchOrganizationPropertyTypes",
  async () => {
    const response = axios.get(propertyTypeByObjectTypeRoute("organization"));
    const { data } = await response;
    return data;
  }
);

export const slice = createSlice({
  name: "propertyTypesAdmin",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPropertyTypes.fulfilled, (state, action) => {
        state.propertyTypes = action.payload;
        state.propertyTypesLoading = "fulfilled";
      })
      .addCase(fetchPropertyTypes.pending, (state) => {
        state.propertyTypesLoading = "pending";
      })
      .addCase(fetchPropertyTypes.rejected, (state) => {
        state.propertyTypesLoading = "rejected";
      })

      .addCase(fetchPropertyTypesByObjectType.fulfilled, (state, action) => {
        state.propertyTypesByObjectType = action.payload;
        state.propertyTypesByObjectTypeLoading = "fulfilled";
      })
      .addCase(fetchPropertyTypesByObjectType.pending, (state) => {
        state.propertyTypesByObjectTypeLoading = "pending";
      })
      .addCase(fetchPropertyTypesByObjectType.rejected, (state) => {
        state.propertyTypesByObjectTypeLoading = "rejected";
      })

      .addCase(fetchPropertyPageSearchTypes.fulfilled, (state, action) => {
        state.propertyPageSearchTypes = action.payload;
        state.propertyPageSearchTypesLoading = "fulfilled";
      })
      .addCase(fetchPropertyPageSearchTypes.pending, (state) => {
        state.propertyPageSearchTypesLoading = "pending";
      })
      .addCase(fetchPropertyPageSearchTypes.rejected, (state) => {
        state.propertyPageSearchTypesLoading = "rejected";
      })
      .addCase(fetchOrganizationPropertyTypes.fulfilled, (state, action) => {
        state.organizationPropertyTypes = action.payload;
        state.organizationPropertyTypesLoading = "fulfilled";
      })
      .addCase(fetchOrganizationPropertyTypes.pending, (state) => {
        state.organizationPropertyTypesLoading = "pending";
      })
      .addCase(fetchOrganizationPropertyTypes.rejected, (state) => {
        state.organizationPropertyTypesLoading = "rejected";
      });
  },
});

const { reducer } = slice;

export default reducer;
